/**
 * Online battles in the game (design D5): the NEUTRAL battle context and the single entry point that starts an online
 * "just" battle from the server's OnlineBattleInit. The client (the room's battle, a rebuild from the log) and the
 * backend's battle simulator (src/server/node/sim-worker.ts) both start online battles only through
 * startOnlineBattle, so they build exactly the same battle.
 *
 * Neutral battle context. The simulation reads the local player's profile (FWP.player) in proc and obj: in a hot-seat
 * battle of the original the player's perks, specialization, implant, tiredness and difficulty act on team 1 (and some
 * perks on every worm). Two clients with different profiles would simulate different battles, so an online battle runs
 * on every client and in the simulator with the same neutral values, as FWP.init_replay swaps in the recorded ones.
 * The fields (and where the battle reads them):
 *  - player.perks = {}: proc.init_gameplay (fast_walk, morale, pe, medic, comandeer), proc.first_move / new_move
 *    (thinker, fast_walk), proc.new_move -> FWP.get_crate_param (luck, luck2, crates, big_guns), proc (engineer),
 *    obj (pe, heavy_guy, sprotsman, accuracy, fire_punch, builder, less_fail, more_fail, flyer, medic, laser_sights);
 *  - player.spec = 0: proc.first_move / new_move (thinker spec 2, fast walk spec 1), FWP.get_crate_param (spec 3),
 *    obj (heavy_guy spec 2, sprotsman spec 1);
 *  - player.cur_impl = 0: obj (implant 7: poison immunity of team 1, 6+: fire immunity of team 1);
 *  - player.tired = 0: obj -> FWP.get_fail_chance (shot failures of team 1), obj's worm sprite (tired >= 100);
 *  - player.settings.skill = 0: proc.init_gameplay (TURNTIME, not for 'just' anyway), obj (+15 / +30 s timeouts);
 *  - player.cur_rip = 0: obj's grave sprite of team 1 (presentation only, neutral for equal screens).
 * The worm names of team 1 (player.worm_name1..8) are not read in a 'just' battle (FWP.get_player_worm_name). Nothing
 * else of the profile reaches a 'just' battle: its teams are built from the barracks (new_interf go_just: attack 0,
 * protect 0, health 100, no spec, no perks) and every team gets the GUM_DEF arsenal (proc.init_gameplay).
 * While the context is active the player object must not be replaced (FWP.apply_state): a client defers profile
 * reloads until the battle is over.
 *
 * Browser-safe: no node imports; types of src/session only.
 */
import type { OnlineBattleInit } from '../../session/online';
import { onlineMapOf, validateOnlineBattleInit } from '../../session/online';
import type { BattleInputSource, BattleRunResult } from './battle-input';

/** The values of the neutral battle context (see the module comment). */
export const NEUTRAL_BATTLE_PLAYER: Readonly<{ perks: Readonly<Record<string, number>>; spec: number; cur_impl: number; tired: number; skill: number; cur_rip: number }> =
  Object.freeze({ perks: Object.freeze({}), spec: 0, cur_impl: 0, tired: 0, skill: 0, cur_rip: 0 });

/** An active neutral battle context; `restore` puts the player's own values back (once). */
export interface NeutralBattleContext {
  readonly active: boolean;
  restore(): void;
}

/**
 * Swaps the neutral values into FWP.player (see NEUTRAL_BATTLE_PLAYER) and returns the handle that restores the
 * player's own values. When the player object was replaced meanwhile (a profile reload), restore leaves the new one
 * alone.
 */
export function enterNeutralBattleContext(fwp: any): NeutralBattleContext {
  const player = fwp.player;
  if (!player.settings || typeof player.settings !== 'object') player.settings = {};
  const saved = {
    perks: player.perks,
    spec: player.spec,
    cur_impl: player.cur_impl,
    tired: player.tired,
    skill: player.settings.skill,
    cur_rip: player.cur_rip,
  };
  player.perks = {};
  player.spec = NEUTRAL_BATTLE_PLAYER.spec;
  player.cur_impl = NEUTRAL_BATTLE_PLAYER.cur_impl;
  player.tired = NEUTRAL_BATTLE_PLAYER.tired;
  player.settings.skill = NEUTRAL_BATTLE_PLAYER.skill;
  player.cur_rip = NEUTRAL_BATTLE_PLAYER.cur_rip;
  let active = true;
  return {
    get active() {
      return active;
    },
    restore() {
      if (!active) return;
      active = false;
      if (fwp.player !== player) return;
      player.perks = saved.perks;
      player.spec = saved.spec;
      player.cur_impl = saved.cur_impl;
      player.tired = saved.tired;
      player.settings.skill = saved.skill;
      player.cur_rip = saved.cur_rip;
    },
  };
}

/**
 * new_interf.barracks for an online battle, exactly as the barracks screen's go_just leaves it: the teams with their
 * worms (attack 0, protect 0, health 100, spec 0, no perks, "Червь #<n>") and the worm limit 8, on the init's PvP map:
 * its terrain, landscape (bg_type), background (bg_mode) and water (ONLINE_MAPS: for maps 1..5 what TEAMS_DEF
 * just1..5 give the offline barracks screen, for the others what every stage on that terrain has). The world builds
 * the rest from the terrain: its limits from the bitmap's width, the spawn points from the terrain's block of
 * WORM_COORDS (world.get_coord_shift) and the seed.
 */
export function onlineBarracks(fwp: any, init: OnlineBattleInit): any {
  const map = onlineMapOf(init)!;
  const barracks: any = { teams: init.teams.length, worms: 0, bg_mode: map.bgMode, bg_type: map.bgType };
  for (let t = 1; t <= 4; t++) {
    const team = init.teams[t - 1];
    barracks['team' + t] = team
      ? { worms: team.worms, cpu_level: team.cpu_level, color: team.color, name: team.name }
      : { worms: 0, cpu_level: 0, color: t, name: '' }; // an unused slot (never shown nor played)
    if (!team) continue;
    barracks.worms += team.worms;
    for (let w = 1; w <= team.worms; w++) {
      barracks['team' + t]['worm' + w] = { attack: 0, protect: 0, health: 100, spec: 0, perks: {}, name: 'Червь #' + w };
    }
  }
  barracks.map = map.mapId;
  barracks.water = map.water;
  barracks.bg_mode = map.bgMode;
  barracks.bg_type = map.bgType;
  barracks.worm_limit = 8;
  return barracks;
}

export interface OnlineBattleOptions {
  /** The server's init of the battle (SessionInfo.online). */
  init: OnlineBattleInit;
  /** The server's seed 0..399. */
  seed: number;
  /** The battle's input source (a LockstepInput for a live battle, a ScriptedInput for a rebuild / the simulator). */
  input: BattleInputSource;
  /** Hash the battle at its start and at every move start (per-turn checkpoints). */
  checkpoints?: boolean;
  /** Called by end_game_fn with the battle's result, after the neutral context was restored. */
  onEnd?: (result: BattleRunResult) => void;
}

/**
 * Starts the online battle `init` with `seed` through the game's own 'just' path (FWP.init_just_play -> map load ->
 * init_game({ mode: 'just' }) with the barracks of the init), in the neutral battle context, with `input` as the
 * battle's input source. The battle starts after the map is loaded (next frames); the neutral context (and the
 * player's own barracks) come back when the battle ends (end_game_fn), or through the returned handle when the caller
 * gives the battle up (abandonOnlineBattle). Throws when the game is busy or the init / seed is invalid.
 */
export function startOnlineBattle(fwp: any, opts: OnlineBattleOptions): NeutralBattleContext {
  if (fwp.main_mode != 'IDLE' || fwp.main_map_dl || fwp._proc || fwp.REPLAY) throw new Error(`startOnlineBattle: the game is busy (${fwp.main_mode})`);
  const err = validateOnlineBattleInit(opts.init);
  if (err) throw new Error(`startOnlineBattle: ${err}`);
  if (!Number.isInteger(opts.seed) || opts.seed < 0 || opts.seed > 399) throw new Error(`startOnlineBattle: invalid seed ${opts.seed}`);
  const init = opts.init;
  const map = onlineMapOf(init)!;
  // The player's own hot-seat barracks come back when the battle is over.
  const ownBarracks = fwp._interf.barracks;
  fwp._interf.barracks = onlineBarracks(fwp, init);
  fwp._interf.cur_element = map.mapIndex;
  fwp._play_id = map.mapIndex;
  const neutral = enterNeutralBattleContext(fwp);
  const context: NeutralBattleContext = {
    get active() {
      return neutral.active;
    },
    restore() {
      if (!neutral.active) return;
      neutral.restore();
      fwp._interf.barracks = ownBarracks;
    },
  };
  fwp.battle_setup = {
    seed: opts.seed,
    annihilators: 0,
    teamName: null,
    input: opts.input,
    checkpoints: opts.checkpoints ?? false,
    onEnd: (result: BattleRunResult) => {
      context.restore();
      if (opts.onEnd) opts.onEnd(result);
    },
    battleId: null,
  };
  try {
    fwp.init_just_play();
  } catch (e) {
    fwp.battle_setup = null;
    context.restore();
    throw e;
  }
  return context;
}

/**
 * Gives a running (or loading) online battle up without a result: the battle is torn down the way the game ends a
 * 'just' battle (end_game_fn, back to the main screen) but its input seam is detached first, so no result is recorded
 * and no onEnd is called; then `context` (startOnlineBattle's handle) restores the player's own values. A client uses
 * it to rebuild the battle from the log (startOnlineBattle again with the same init, seed and coordinator: the known
 * ticks are fast-forwarded) after its simulation used input the server dropped or diverged.
 */
export function abandonOnlineBattle(fwp: any, context: NeutralBattleContext): void {
  fwp.battle_setup = null;
  // Still loading the map: nothing starts when it arrives (FWP.map_downloaded only removes the wait splash).
  if (fwp.main_map_dl && fwp.main_execute_after_map_dl == 'continue preparing just play') fwp.main_execute_after_map_dl = '';
  if (fwp._proc && fwp.main_mode == 'LOCAL_GAME' && fwp._play_mode == 'just') {
    fwp.battle_input = null;
    fwp._proc.battle_input = null;
    fwp.end_game_fn(0);
  }
  context.restore();
}
